import { useCallback, useEffect, useState } from 'react';
import { ENDPOINTS } from 'features/ai-assistant/constants';
import { ALT_API_CLIENT } from 'services/API';
import { UIStatus } from 'UI/constants/status';

const SESSIONS_PER_PAGE = 20;

export const useListConversation = () => {
  const [conversations, setConversations] = useState([]);
  const [uiStatus, setUiStatus] = useState(UIStatus.Loading);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    hasMore: true,
    isLoadingMore: false
  });

  const fetchConversations = useCallback(async () => {
    setUiStatus(UIStatus.Loading);

    try {
      const response = await ALT_API_CLIENT.get(ENDPOINTS.sessions, {
        params: {
          page: 1,
          perPage: SESSIONS_PER_PAGE
        }
      });

      const data = response.data?.data || [];
      const total = Number(response.data?.total);
      setConversations(data);
      setUiStatus(data.length > 0 ? UIStatus.Success : UIStatus.Empty);

      const hasMore = SESSIONS_PER_PAGE < total;
      setPaginationState({
        page: 1,
        hasMore,
        isLoadingMore: false
      });
    } catch (error) {
      setUiStatus(UIStatus.Error);
      setPaginationState(prev => ({ ...prev, hasMore: false }));
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMoreConversations = useCallback(async () => {
    if (!paginationState.hasMore || paginationState.isLoadingMore) return;

    const nextPage = paginationState.page + 1;
    setPaginationState(prev => ({ ...prev, isLoadingMore: true }));

    try {
      const response = await ALT_API_CLIENT.get(ENDPOINTS.sessions, {
        params: {
          page: nextPage,
          perPage: SESSIONS_PER_PAGE
        }
      });

      const newConversations = response.data?.data || [];
      const total = Number(response.data?.total);
      const hasMore = nextPage * SESSIONS_PER_PAGE < total;

      if (newConversations.length > 0) {
        setConversations(prev => [...prev, ...newConversations]);
      }

      setPaginationState({
        page: nextPage,
        hasMore,
        isLoadingMore: false
      });
    } catch (error) {
      setPaginationState(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [paginationState]);

  const updateConversations = useCallback(action => {
    setConversations(prevConversations => {
      return typeof action === 'function' ? action(prevConversations) : action;
    });
  }, []);

  const pinConversation = useCallback(async sessionId => {
    try {
      const endpoint = ENDPOINTS.sessionPin.replace(':sessionId', sessionId);
      await ALT_API_CLIENT.put(endpoint);
      return true;
    } catch {
      return false;
    }
  }, []);

  const unpinConversation = useCallback(async sessionId => {
    try {
      const endpoint = ENDPOINTS.sessionUnpin.replace(':sessionId', sessionId);
      await ALT_API_CLIENT.put(endpoint);
      return true;
    } catch {
      return false;
    }
  }, []);

  const renameConversation = useCallback(async (sessionId, sessionTitle) => {
    try {
      const endpoint = ENDPOINTS.sessionUpdate.replace(':sessionId', sessionId);
      await ALT_API_CLIENT.put(endpoint, { sessionTitle });
      return true;
    } catch {
      return false;
    }
  }, []);

  const deleteConversation = useCallback(async sessionId => {
    try {
      const endpoint = ENDPOINTS.sessionDelete.replace(':sessionId', sessionId);
      await ALT_API_CLIENT.delete(endpoint);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    conversations,
    deleteConversation,
    fetchConversations,
    fetchMoreConversations,
    hasMoreConversations: paginationState.hasMore,
    isLoadingMoreConversations: paginationState.isLoadingMore,
    pinConversation,
    renameConversation,
    uiStatus,
    unpinConversation,
    updateConversations
  };
};
